"use client";

// Per-chat follow-up queue. In-memory only (survives `key={chatId}` remount,
// dies on full reload). Never persist file parts — they carry short-lived
// SAS URLs. Drain lock + atomic take prevent Strict Mode double-sends.
//
// A reload with items still queued therefore abandons their upload rows: the
// composer already dropped the tiles, so `useAttachments`' unmount sweep no
// longer knows about them. They stay `staging` and the hourly
// /api/scheduled/cleanup-uploads reaper collects them (24h), which is the
// same backstop a crashed upload relies on.
import { c as _c } from "react/compiler-runtime";
import { nanoid } from "nanoid";
import { useSyncExternalStore } from "react";
import type { EditorPart } from "./serialize";
export const MAX_FOLLOW_UPS = 20;
export type QueuedFollowUp = {
  id: string;
  parts: EditorPart[];
};
export type FollowUpEditSlot = {
  id: string;
  index: number;
  parts: EditorPart[];
};
const itemsByChat = new Map<string, QueuedFollowUp[]>();
const listenersByChat = new Map<string, Set<() => void>>();
const editSlots = new Map<string, FollowUpEditSlot>();
const drainLocks = new Map<string, object>();
const EMPTY: readonly QueuedFollowUp[] = [];
function emit(chatId: string) {
  const listeners = listenersByChat.get(chatId);
  if (!listeners) {
    return;
  }
  for (const listener of [...listeners]) {
    listener();
  }
}
function getItems(chatId: string): QueuedFollowUp[] {
  return itemsByChat.get(chatId) ?? [];
}
function setItems(chatId: string, items: QueuedFollowUp[]) {
  if (items.length === 0) {
    itemsByChat.delete(chatId);
  } else {
    itemsByChat.set(chatId, items);
  }
  emit(chatId);
}
export function subscribeFollowUps(chatId: string, listener: () => void): () => void {
  let listeners = listenersByChat.get(chatId);
  if (!listeners) {
    listeners = new Set();
    listenersByChat.set(chatId, listeners);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      listenersByChat.delete(chatId);
    }
  };
}
export function listFollowUps(chatId: string): readonly QueuedFollowUp[] {
  return itemsByChat.get(chatId) ?? EMPTY;
}
export function enqueueFollowUp(chatId: string, parts: EditorPart[]): QueuedFollowUp | null {
  const current = getItems(chatId);
  if (current.length >= MAX_FOLLOW_UPS) {
    return null;
  }
  const item: QueuedFollowUp = {
    id: nanoid(),
    parts: parts.slice()
  };
  setItems(chatId, [...current, item]);
  return item;
}
export function removeFollowUp(chatId: string, id: string): QueuedFollowUp | null {
  const current = getItems(chatId);
  const item = current.find(entry => entry.id === id) ?? null;
  if (!item) {
    return null;
  }
  setItems(chatId, current.filter(entry => entry.id !== id));
  return item;
}
export function moveFollowUp(chatId: string, id: string, direction: "up" | "down"): void {
  const current = getItems(chatId);
  const index = current.findIndex(item => item.id === id);
  if (index < 0) {
    return;
  }
  moveFollowUpTo(chatId, id, direction === "up" ? index - 1 : index + 2);
}

/** Insert before `toIndex` in the current list (including the moving item). */
export function moveFollowUpTo(chatId: string, id: string, toIndex: number): void {
  const current = getItems(chatId);
  const from = current.findIndex(item => item.id === id);
  if (from < 0) {
    return;
  }
  const destRaw = Math.max(0, Math.min(toIndex, current.length));
  if (from === destRaw || from === destRaw - 1) {
    return;
  }
  const next = current.slice();
  const [item] = next.splice(from, 1);
  if (!item) {
    return;
  }
  const dest = destRaw > from ? destRaw - 1 : destRaw;
  next.splice(dest, 0, item);
  setItems(chatId, next);
}
export function insertFollowUpAt(chatId: string, index: number, item: QueuedFollowUp): void {
  const current = getItems(chatId).filter(entry => entry.id !== item.id);
  const dest = Math.max(0, Math.min(index, current.length));
  const next = current.slice();
  next.splice(dest, 0, {
    id: item.id,
    parts: item.parts.slice()
  });
  setItems(chatId, next);
}

/** Atomic shift of the head item. Used by the drain lock. */
export function takeFollowUp(chatId: string): QueuedFollowUp | null {
  const current = getItems(chatId);
  if (current.length === 0) {
    return null;
  }
  const [head, ...rest] = current;
  setItems(chatId, rest);
  return head ?? null;
}
export function unshiftFollowUp(chatId: string, item: QueuedFollowUp): void {
  const current = getItems(chatId).filter(entry => entry.id !== item.id);
  setItems(chatId, [item, ...current]);
}
export function tryLockDrain(chatId: string, owner: object = {}): boolean {
  if (drainLocks.has(chatId)) {
    return false;
  }
  drainLocks.set(chatId, owner);
  emit(chatId);
  return true;
}

/**
 * Frees `chatId`'s drain lock. With `owner`, only if that owner still holds
 * it — a late release from an unmounted session must not free a lock a newer
 * session has since taken. Without, unconditionally.
 */
export function unlockDrain(chatId: string, owner?: object): void {
  const holder = drainLocks.get(chatId);
  if (holder === undefined || owner !== undefined && holder !== owner) {
    return;
  }
  drainLocks.delete(chatId);
  emit(chatId);
}

/**
 * Whether a drain holds `chatId`'s lock. Subscribing is what lets a session
 * that found the lock held retry once it frees — a lock released late by an
 * unmounted session otherwise changes nothing a mounted one renders on.
 */
export function useDrainLocked(chatId) {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] !== chatId) {
    t0 = listener => subscribeFollowUps(chatId, listener);
    t1 = () => drainLocks.has(chatId);
    $[0] = chatId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  return useSyncExternalStore(t0, t1, _temp);
}
function _temp() {
  return false;
}
export function useFollowUpQueue(chatId) {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] !== chatId) {
    t0 = listener => subscribeFollowUps(chatId, listener);
    t1 = () => listFollowUps(chatId);
    $[0] = chatId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  return useSyncExternalStore(t0, t1, _temp2);
}
function _temp2() {
  return EMPTY;
}
export function resetFollowUpQueuesForTests(): void {
  itemsByChat.clear();
  listenersByChat.clear();
  editSlots.clear();
  drainLocks.clear();
}
export function getFollowUpEditSlot(chatId: string): FollowUpEditSlot | null {
  return editSlots.get(chatId) ?? null;
}
export function useFollowUpEditSlot(chatId) {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] !== chatId) {
    t0 = listener => subscribeFollowUps(chatId, listener);
    t1 = () => editSlots.get(chatId) ?? null;
    $[0] = chatId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  return useSyncExternalStore(t0, t1, _temp3);
}

/** Pull an item out of the queue and remember its slot for a later insert. */
function _temp3() {
  return null;
}
export function beginFollowUpEdit(chatId: string, id: string): FollowUpEditSlot | null {
  const current = getItems(chatId);
  const index = current.findIndex(item => item.id === id);
  const item = index < 0 ? null : current[index] ?? null;
  if (!item) {
    return null;
  }
  const slot: FollowUpEditSlot = {
    id: item.id,
    index,
    parts: item.parts.slice()
  };
  editSlots.set(chatId, slot);
  setItems(chatId, current.filter(entry => entry.id !== id));
  return slot;
}
export function takeFollowUpEditSlot(chatId: string): FollowUpEditSlot | null {
  const slot = editSlots.get(chatId) ?? null;
  if (!slot) {
    return null;
  }
  editSlots.delete(chatId);
  emit(chatId);
  return slot;
}

/** Keep parked file parts when the composer submit doesn't re-stage them. */
export function mergeFollowUpParts(incoming: EditorPart[], parked: EditorPart[]): EditorPart[] {
  const incomingUploadIds = new Set(queuedFollowUpUploadIds(incoming));
  const parkedFiles = parked.filter(part => {
    if (part.type !== "file") {
      return false;
    }
    const uploadId = part.data?.uploadId;
    return !uploadId || !incomingUploadIds.has(uploadId);
  });
  return [...incoming, ...parkedFiles];
}

/** Human-readable row label — skill/artifact names, not `{{skill:…}}` tokens. */
export function queuedFollowUpLabel(parts: EditorPart[]): string {
  const text = parts.map(part => {
    if (part.type === "text") {
      return part.text;
    }
    if (part.type === "data-skill-reference") {
      return part.data.skillName;
    }
    if (part.type === "data-artifact-reference") {
      return part.data.artifactName;
    }
    return "";
  }).join("").replace(/\s+/g, " ").trim();
  if (text) {
    return text;
  }
  const file = parts.find(part => part.type === "file");
  return file && file.type === "file" ? file.filename : "Attachment";
}
export function queuedFollowUpUploadIds(parts: EditorPart[]): string[] {
  return parts.flatMap(part => part.type === "file" && part.data?.uploadId ? [part.data.uploadId] : []);
}